import { useCallback, useEffect, useState } from 'react';
import { t, formatNumber, formatPercent, formatDateTime, traitKey, type ScientistsState, type TechArea } from '@galaxis/shared';
import { fetchScientists, hireScientist, assignScientist, dismissScientist, fetchResources } from '../../api/galaxy.ts';
import { useGameStore } from '../../store/game-store.ts';
import { previewResourceSpend, useLiveResources } from '../../economy/live-store.ts';
import { AREAS, AREA_META } from './research-format.ts';

/** The language of the client (formatting follows it; the text catalog is chosen by the later i18n work). */
const LOCALE = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'hu';
const BTN = 'btn-primary min-h-11 w-full text-xs disabled:opacity-40';
const BTN_GHOST = 'min-h-11 px-3 rounded border border-slate-600 text-xs text-slate-300 hover:border-slate-400 disabled:opacity-40';
const areaName = (a: TechArea) => t(`research.area.${a}`);
const num = (n: number) => formatNumber(n, LOCALE);

/** Scientists: the second research-point source (leads of an area), plus the survey data total. All texts come from the catalog. */
export function ScientistsPanel({ version, onChanged }: { version: number; onChanged: () => void }) {
  const [st, setSt] = useState<ScientistsState | null>(null);
  const [busy, setBusy] = useState(false);
  const { pushToast, setResources } = useGameStore();
  const live = useLiveResources();
  const load = useCallback(() => { fetchScientists().then(setSt).catch(() => setSt(null)); }, []);
  useEffect(() => { load(); }, [load, version]);

  const run = async (fn: () => Promise<string | void>) => {
    setBusy(true);
    try { const m = await fn(); if (m) pushToast(m); setResources(await fetchResources()); load(); onChanged(); }
    catch (e) { pushToast(e instanceof Error ? t(e.message) : t('research.error.not_found'), 'error'); load(); }
    finally { setBusy(false); }
  };

  if (!st) return null;
  if (!st.enabled) return (
    <section className="sci-fi-panel rounded-xl p-4" aria-label="scientists" data-testid="scientists">
      <p className="text-xs text-slate-500">{t('research.sci.disabled')}</p>
    </section>
  );
  const full = st.scientists.length >= st.capacity;
  return (
    <section className="sci-fi-panel rounded-xl p-4" aria-label="scientists" data-testid="scientists">
      <h2 className="text-sm font-semibold text-slate-300">{t('research.sci.title', { count: st.scientists.length, capacity: st.capacity })}</h2>
      <p className="text-[11px] text-slate-500">{t('research.sci.intro')}</p>
      <p className="text-[11px] text-slate-500 mb-2">
        <span data-testid="sci-upkeep" data-value={st.upkeepEnergyPerHour}>{t('research.sci.upkeep', { value: num(st.upkeepEnergyPerHour) })}</span>{' · '}
        <span data-testid="sci-survey" data-value={st.surveyData.total}>{t('research.sci.survey', { value: num(st.surveyData.total), cap: num(st.surveyData.cap) })}</span>
      </p>

      <ul className="space-y-2" data-testid="sci-list">
        {st.scientists.length === 0 && <li className="text-xs text-slate-500">{t('research.sci.empty')}</li>}
        {st.scientists.map(s => (
          <li key={s.id} className="rounded-lg border border-slate-700 p-3" data-testid={`sci-${s.id}`} data-area={s.assignedArea ?? ''} data-species={s.speciesId}>
            <div className="flex justify-between gap-2 text-sm">
              <span className="text-slate-100 font-medium" lang="und">{s.name}</span>
              <span className="text-xs text-slate-400">{t('research.sci.levelBonus', { level: s.level, bonus: formatPercent(s.rpBonus, LOCALE) })}</span>
            </div>
            <p className="text-[11px] text-slate-400">
              {t('research.sci.specialty', { area: areaName(s.area) })} · <b className="text-accent">{t(traitKey(s.trait, 'name'))}</b>
              {s.focus ? ` ${t('research.sci.focus', { theme: t(`research.theme.${s.focus}`) })}` : ''} — {t(traitKey(s.trait, 'blurb'))}
            </p>
            <div className="mt-1 h-1.5 rounded bg-slate-800 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={s.xpToNext === null ? 100 : 50} aria-label={t('research.sci.xpBar')}>
              <div className="h-full bg-accent" style={{ width: s.xpToNext === null ? '100%' : `${Math.max(5, 100 - Math.min(100, s.xpToNext))}%` }} />
            </div>
            <p className="text-[11px] text-slate-500">
              {s.xpToNext === null ? t('research.sci.xpMax') : t('research.sci.xpNext', { xp: num(s.xp), next: num(s.xpToNext) })}
              {' · '}{t('research.sci.upkeepRetire', { upkeep: num(s.upkeepEnergy), when: formatDateTime(s.retiresAt, LOCALE) })}
            </p>
            <div role="radiogroup" aria-label={t('research.sci.assignAria', { name: s.name })} className="mt-2 flex flex-wrap gap-2">
              {AREAS.map(a => (
                <button key={a} type="button" role="radio" aria-checked={s.assignedArea === a} disabled={busy} data-testid={`assign-${s.id}-${a}`}
                  className={`min-h-11 px-3 rounded border text-xs ${s.assignedArea === a ? 'border-accent text-accent' : 'border-slate-600 text-slate-300'} disabled:opacity-40`}
                  onClick={() => void run(async () => { await assignScientist(s.id, s.assignedArea === a ? null : a); return s.assignedArea === a ? t('research.sci.toReserve', { name: s.name }) : t('research.sci.appointed', { name: s.name, area: areaName(a) }); })}>
                  {AREA_META[a].icon} {areaName(a)}
                </button>
              ))}
              <button type="button" className={BTN_GHOST} disabled={busy} data-testid={`dismiss-${s.id}`}
                onClick={() => void run(async () => { await dismissScientist(s.id); return t('research.sci.dismissed', { name: s.name }); })}>{t('research.sci.dismiss')}</button>
            </div>
          </li>
        ))}
      </ul>

      <h3 className="mt-3 text-xs font-semibold text-slate-400">{t('research.sci.candidates')} <span className="font-normal text-slate-500">({t('research.sci.refresh', { when: formatDateTime(st.nextOfferAt, LOCALE) })})</span></h3>
      <ul className="mt-1 space-y-2" data-testid="sci-candidates">
        {st.candidates.length === 0 && <li className="text-xs text-slate-500">{t('research.sci.noCandidates')}</li>}
        {st.candidates.map(c => {
          const afford = !!live && live.metal >= c.cost.metal && live.energy >= c.cost.energy;
          const why = full ? t('research.sci.whyFull') : !afford ? t('research.sci.whyAfford') : null;
          return (
            <li key={`${c.offerSeq}-${c.slot}`} className="rounded-lg border border-slate-700 p-3" data-testid={`cand-${c.slot}`} data-species={c.speciesId}>
              <div className="flex justify-between gap-2 text-sm"><span className="text-slate-100" lang="und">{c.name}</span><span className="text-xs text-slate-400">{t('research.sci.level', { level: c.level })}</span></div>
              <p className="text-[11px] text-slate-400">
                {AREA_META[c.area].icon} {areaName(c.area)} · <b className="text-accent">{t(traitKey(c.trait, 'name'))}</b>
                {c.focus ? ` ${t('research.sci.focus', { theme: t(`research.theme.${c.focus}`) })}` : ''} — {t(traitKey(c.trait, 'blurb'))}
              </p>
              <p className="text-[11px] font-mono text-slate-400">{t('research.sci.cost', { metal: num(c.cost.metal), energy: num(c.cost.energy), upkeep: num(c.upkeepEnergy) })}</p>
              <button type="button" className={`${BTN} mt-2`} disabled={busy || !!why} title={why ?? undefined} data-testid={`hire-${c.slot}`}
                onClick={() => void run(async () => { previewResourceSpend(c.cost.metal, c.cost.energy, 0); await hireScientist(c.offerSeq, c.slot); return t('research.sci.hired', { name: c.name }); })}>
                {t('research.sci.hire')}
              </button>
              {why && <p className="text-[11px] text-amber-400 mt-1">{why}</p>}
            </li>
          );
        })}
      </ul>
      <p className="mt-2 text-[11px] text-slate-500">{t('research.sci.surveyNote')}</p>
    </section>
  );
}
